import { useState, useCallback } from 'react';
import { useWorkspaceStore } from '../../../../stores/workspaceStore';
import { socialAccountService } from '../../../../services/socialAccountService';
import { useToast } from '../../../../hooks/useToast';
import type { SocialPlatform } from '../components/PlatformIcon';

interface UseConnectPlatformReturn {
  /** Initiate the OAuth flow for the selected platform */
  connect: (platform: SocialPlatform) => Promise<void>;
  /** Whether the connection request is in progress */
  isConnecting: boolean;
  /** Error message if connection initiation failed */
  error: string | null;
}

/**
 * Hook for initiating the OAuth connection flow for a social platform.
 *
 * Calls the API to generate an OAuth authorization URL, then redirects
 * the browser to the platform's OAuth consent page.
 *
 * Stores a platform hint in localStorage so the callback page knows
 * which backend endpoint to use for the code exchange.
 */
export function useConnectPlatform(): UseConnectPlatformReturn {
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const currentWorkspace = useWorkspaceStore((s) => s.currentWorkspace);
  const toast = useToast();

  const connect = useCallback(
    async (platform: SocialPlatform) => {
      if (!currentWorkspace?.id) {
        toast.error('Error', 'No workspace selected. Please select a workspace first.');
        return;
      }

      setIsConnecting(true);
      setError(null);

      try {
        const result = await socialAccountService.getConnectUrl(
          currentWorkspace.id,
          platform
        );

        if (result.authorizationUrl) {
          // Store the platform hint so the callback page knows which endpoint to use
          try {
            localStorage.setItem('oauth_callback_platform', platform);
          } catch {
            // localStorage not available, callback will fall back to detection
          }

          // Redirect the browser to the OAuth authorization page
          window.location.href = result.authorizationUrl;
        } else {
          throw new Error('No authorization URL received from the server.');
        }
      } catch (err: unknown) {
        const message =
          err instanceof Error
            ? err.message
            : 'Failed to initiate connection. Please try again.';
        setError(message);
        toast.error('Connection Failed', message);
        setIsConnecting(false);
      }
      // Note: We don't setIsConnecting(false) on success because the browser is redirecting
    },
    [currentWorkspace?.id, toast]
  );

  return {
    connect,
    isConnecting,
    error,
  };
}